import React, { useEffect, useState } from 'react';
import { createSupportTicket, installSupportDiagnostics, type SupportTicketCategory, type SupportTicketPriority, type SupportTicketSurface } from '../services/supportTickets';
import './SupportTicketLauncher.css';

const SupportTicketLauncher: React.FC<{ surface: SupportTicketSurface; pageLabel: string }> = ({ surface, pageLabel }) => {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<SupportTicketCategory>('SOFTWARE');
  const [priority, setPriority] = useState<SupportTicketPriority>('NORMAL');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdNumber, setCreatedNumber] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);

  useEffect(() => installSupportDiagnostics(), []);

  const close = () => {
    if (submitting) return;
    setOpen(false);
    setError(null);
    setCreatedNumber(null);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await createSupportTicket({ applicationSurface: surface, pagePath: window.location.hash || window.location.pathname, pageLabel, category, priority, subject, description, files });
      setCreatedNumber(result.ticket.ticketNumber);
      setSubject('');
      setDescription('');
      setCategory('SOFTWARE');
      setPriority('NORMAL');
      setFiles([]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Support ticket could not be created');
    } finally {
      setSubmitting(false);
    }
  };

  return <>
    <button type="button" className="support-ticket-launcher-button" onClick={() => setOpen(true)}>Support</button>
    {open && <div className="support-ticket-backdrop" role="presentation">
      <section className="support-ticket-dialog" role="dialog" aria-modal="true" aria-labelledby="support-ticket-title">
        <div className="support-ticket-heading"><div><div className="support-ticket-eyebrow">BONZAIQ SUPPORT</div><h2 id="support-ticket-title">How can we help?</h2><p>From {pageLabel}. Safe diagnostic context will be included.</p></div><button type="button" className="support-ticket-secondary" onClick={close}>Close</button></div>
        {createdNumber ? <div className="support-ticket-success"><strong>Support request sent</strong><span>Ticket {createdNumber} has been created.</span><button type="button" className="support-ticket-primary" onClick={close}>Done</button></div> : <form onSubmit={submit}>
          {error && <div className="support-ticket-error">{error}</div>}
          <div className="support-ticket-columns">
            <label>Category<select value={category} onChange={event => setCategory(event.target.value as SupportTicketCategory)}><option value="SOFTWARE">Software problem</option><option value="ACCESS">Access or account</option><option value="LICENSING">Licensing or seats</option><option value="DATA">Data problem</option><option value="OTHER">Other</option></select></label>
            <label>Priority<select value={priority} onChange={event => setPriority(event.target.value as SupportTicketPriority)}><option value="LOW">Low</option><option value="NORMAL">Normal</option><option value="HIGH">High</option><option value="URGENT">Urgent</option></select></label>
          </div>
          <label>Subject<input value={subject} maxLength={160} onChange={event => setSubject(event.target.value)} required /></label>
          <label>What happened?<textarea value={description} maxLength={10000} onChange={event => setDescription(event.target.value)} required /></label>
          <label className="support-ticket-file-picker">Screenshots or supporting files<input type="file" multiple onChange={event => setFiles(Array.from(event.target.files || []).slice(0, 8))} /><span>Choose up to 8 files, 25 MB each.</span></label>
          {files.length > 0 && <div className="support-ticket-file-list">{files.map((file, index) => <span key={`${file.name}-${index}`}><b>{file.name}</b><small>{file.size < 1024 * 1024 ? `${(file.size / 1024).toFixed(1)} KB` : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}</small><button type="button" onClick={() => setFiles(current => current.filter((_, itemIndex) => itemIndex !== index))}>Remove</button></span>)}</div>}
          <div className="support-ticket-privacy">Includes the current page, browser details, and recent application errors. It does not copy page contents, form values, documents, passwords, or authentication tokens.</div>
          <div className="support-ticket-actions"><button type="button" className="support-ticket-secondary" onClick={close}>Cancel</button><button type="submit" className="support-ticket-primary" disabled={submitting}>{submitting ? 'Sending…' : 'Send support request'}</button></div>
        </form>}
      </section>
    </div>}
  </>;
};

export default SupportTicketLauncher;
